// AGENT-A7: View Selectors (two-up).
//
// Two SIDE-BY-SIDE tab strips per the period-selector spec:
//
//   ┌─ by Number of Quotes / Orders ─────┐  ┌─ by Quantity of Units Quoted / Sold ┐
//   │ Controls quote and order COUNTS …  │  │ Controls UNIT quantities …          │
//   │ ┌──┐┌─────┐┌─────┐                 │  │ ┌──┐┌─────┐┌─────┐                  │
//   │ │FY││YoY  ││QoQ  │                 │  │ │FY││YoY  ││QoQ  │                  │
//   │ └──┘└─────┘└─────┘                 │  │ └──┘└─────┘└─────┘                  │
//   │ [FY26 ▾]  ← only when FY active    │  │ [FY26 ▾]  ← only when FY active     │
//   └────────────────────────────────────┘  └─────────────────────────────────────┘
//
// LEFT card drives `quotesOrdersChart` + the count-variant pivot.
// RIGHT card drives `unitsQuotedSoldChart` + the units-variant pivot.
//
// Wiring:
//   - filterStore.viewLeft  / setView("left", ...)   — tab selection
//   - filterStore.viewRight / setView("right", ...)
//   - filterStore.fyLeft    / setFyLeft([...])       — FY dropdown (left)
//   - filterStore.fyRight   / setFyRight([...])      — FY dropdown (right)
//   - filterStore.dateRange.years (via setDateRange) — mirrored from the
//     currently-active FY dropdown so the existing /report path applies the
//     filter. The left card wins when both are FY; the right card wins
//     otherwise. (Either way the union is sent for clarity.)
//   - filterStore.setPivotVisualId — flips the metrics-table pivot id to
//     match the active view (kept from AGENT-BD).
//
// Periods come from GET /api/dashboard/periods via `usePeriods()` (Agent 2
// owns the endpoint; client.ts has an offline fallback).

import { useEffect, useMemo } from "react";
import { usePeriods } from "../api/client";
import { useFilterStore } from "../state/filterStore";
import type { DashboardView } from "../types";
import "./ViewSelectors.css";

interface TabSpec {
  key: DashboardView;
  label: string;
  hint?: string;
}

const TABS: ReadonlyArray<TabSpec> = [
  { key: "fy",  label: "FY" },
  { key: "yoy", label: "YoY (Calendar Year)", hint: "Year-over-year — one row per calendar year" },
  { key: "qoq", label: "QoQ (Calendar Year)", hint: "Quarter-over-quarter — one row per calendar quarter" },
];

const VIEW_SHORT_LABEL: Record<DashboardView, string> = {
  fy:  "FY",
  yoy: "YoY",
  qoq: "QoQ",
};

/**
 * Pivot-visual-id mapping for the tabs.
 *
 * - `fy`  → "532f1cbdb173212cf1be": same Year + Parent Product layout as YoY.
 *           The static PBI Fiscal-Year pivot (58fda9ae53be1141ec5d) only
 *           ships Qty Quoted/Sold columns, which the user prefers to skip in
 *           favor of the richer Issued/Won/Orders Released/Revenue layout.
 *           The FY dropdown still narrows dateRange.years so the table is
 *           scoped to the selected fiscal year(s).
 * - `yoy` → "532f1cbdb173212cf1be": HSQ YoY Calendar-Year pivot.
 * - `qoq` → "89f4195d438b1f143a5c": HSQ QoQ Calendar-Year pivot.
 */
const PIVOT_VISUAL_ID_BY_VIEW: Record<DashboardView, string> = {
  fy:  "532f1cbdb173212cf1be",
  yoy: "532f1cbdb173212cf1be",
  qoq: "89f4195d438b1f143a5c",
};

interface ViewSelectorCardProps {
  side: "left" | "right";
  title: string;
  value: DashboardView;
  fySelection: string[];
  fyOptions: string[];
  fyLoading: boolean;
  onTabChange: (v: DashboardView) => void;
  onFyChange: (years: string[]) => void;
}

function ViewSelectorCard({
  side,
  title,
  value,
  fySelection,
  fyOptions,
  fyLoading,
  onTabChange,
  onFyChange,
}: ViewSelectorCardProps): JSX.Element {
  const showFyPicker = value === "fy";
  // The picker is single-select for now (matches the offline contract's
  // `defaultFy: string`). The store still holds an array so we can promote
  // to multi-select later without another schema change.
  const fySingle = fySelection[0] ?? "";

  // HSQ-tweak (2026-05-27): compact single-row layout — title, tabs, and the
  // FY picker share one line so the card no longer wastes vertical space on a
  // long subtitle + stacked rows.
  return (
    <div className="a7-vs-card" data-side={side}>
      <div className="a7-vs-head">
        <h3 className="dash-h3 a7-vs-title">{title}</h3>
        <div className="a7-vs-tabs" role="tablist" aria-label={title}>
          {TABS.map((t) => {
            const selected = t.key === value;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`a7-vs-tab${selected ? " a7-vs-tab--selected" : ""}`}
                onClick={() => onTabChange(t.key)}
                data-side={side}
                data-view={t.key}
                title={t.hint}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        {showFyPicker && (
          <div className="a7-vs-fy-row">
            <label className="a7-vs-fy-label" htmlFor={`a7-vs-fy-${side}`}>
              Fiscal Year
            </label>
            <select
              id={`a7-vs-fy-${side}`}
              className="a7-vs-fy-select"
              value={fySingle}
              disabled={fyLoading || fyOptions.length === 0}
              onChange={(e) => {
                const v = e.target.value;
                onFyChange(v ? [v] : []);
              }}
              data-side={side}
            >
              {fyOptions.length === 0 ? (
                <option value="">{fyLoading ? "Loading…" : "No periods"}</option>
              ) : (
                <>
                  <option value="">All available</option>
                  {fyOptions.map((fy) => (
                    <option key={fy} value={fy}>
                      {fy}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

export function ViewSelectors(): JSX.Element {
  const viewLeft = useFilterStore((s) => s.viewLeft);
  const viewRight = useFilterStore((s) => s.viewRight);
  const fyLeft = useFilterStore((s) => s.fyLeft);
  const fyRight = useFilterStore((s) => s.fyRight);
  const dateRange = useFilterStore((s) => s.dateRange);
  const setView = useFilterStore((s) => s.setView);
  const setFyLeft = useFilterStore((s) => s.setFyLeft);
  const setFyRight = useFilterStore((s) => s.setFyRight);
  const setDateRange = useFilterStore((s) => s.setDateRange);
  // AGENT-BD: pivot-visual setter, kept so flipping a tab also flips the
  // metrics-table pivot id.
  const setPivotVisualId = useFilterStore((s) => s.setPivotVisualId);

  const periodsQuery = usePeriods();
  const fyOptions = periodsQuery.data?.fy ?? [];
  const fyDefault = periodsQuery.data?.defaultFy ?? null;

  // Seed each FY dropdown with the backend's defaultFy on first successful
  // load, only when the user hasn't already picked something. Mirror that
  // initial pick into dateRange.years too, so /report sees the filter.
  useEffect(() => {
    if (!periodsQuery.isSuccess || !fyDefault) return;
    if (fyLeft.length === 0) setFyLeft([fyDefault]);
    if (fyRight.length === 0) setFyRight([fyDefault]);
    // Only seed dateRange.years if it's empty AND at least one side is FY.
    if (
      dateRange.years.length === 0 &&
      (viewLeft === "fy" || viewRight === "fy")
    ) {
      setDateRange({ ...dateRange, years: [fyDefault] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodsQuery.isSuccess, fyDefault]);

  // Mirror the union of (active-FY-side) selections into dateRange.years
  // every time the per-side FY arrays change. This keeps the existing
  // /report contract working — the dateRange.years field is still the
  // single source of truth for backend FY filtering.
  const mirroredYears = useMemo(() => {
    const merged = new Set<string>();
    if (viewLeft === "fy") fyLeft.forEach((y) => merged.add(y));
    if (viewRight === "fy") fyRight.forEach((y) => merged.add(y));
    return Array.from(merged).sort();
  }, [viewLeft, viewRight, fyLeft, fyRight]);

  useEffect(() => {
    // Avoid an infinite loop: only push if the array actually differs.
    const same =
      mirroredYears.length === dateRange.years.length &&
      mirroredYears.every((y, i) => dateRange.years[i] === y);
    if (same) return;
    setDateRange({ ...dateRange, years: mirroredYears });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mirroredYears]);

  // Mixed-view prevention: clicking a tab on either side updates BOTH cards
  // so the two views stay in lock-step. The reconcile effect below catches
  // any other path that might desync them (e.g., direct store mutation).
  const handleLeftTab = (v: DashboardView): void => {
    setView("left", v);
    setView("right", v);
    const nextId = PIVOT_VISUAL_ID_BY_VIEW[v];
    if (nextId) setPivotVisualId(nextId);
  };
  const handleRightTab = (v: DashboardView): void => {
    setView("left", v);
    setView("right", v);
    const nextId = PIVOT_VISUAL_ID_BY_VIEW[v];
    if (nextId) setPivotVisualId(nextId);
  };

  // FY picker sync: both cards feed the SAME global date filter
  // (`dateRange.years` is the UNION of fyLeft/fyRight), so a divergent
  // left/right selection silently widens the filter — picking FY24 on the
  // left while the right still holds FY26 returns BOTH fiscal years. Keep the
  // two pickers in lock-step exactly like the view tabs: setting either side
  // updates both, so the union is always a single FY.
  const handleFyChange = (years: string[]): void => {
    setFyLeft(years);
    setFyRight(years);
  };

  // Reconcile effect: if the two sides ever diverge, re-sync immediately.
  // Tie-break: left wins if it's "fy", else right wins.
  useEffect(() => {
    if (viewLeft === viewRight) return;
    const winner: DashboardView = viewLeft === "fy" ? viewLeft : viewRight;
    if (viewLeft !== winner) setView("left", winner);
    if (viewRight !== winner) setView("right", winner);
    const nextId = PIVOT_VISUAL_ID_BY_VIEW[winner];
    if (nextId) setPivotVisualId(nextId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewLeft, viewRight]);

  // Suppress unused-import warning for the short-label map (kept for future
  // re-introduction if mixed-view ever becomes valid again).
  void VIEW_SHORT_LABEL;

  return (
    <div className="a7-vs-grid">
      <ViewSelectorCard
        side="left"
        title="Period — Counts View"
        value={viewLeft}
        fySelection={fyLeft}
        fyOptions={fyOptions}
        fyLoading={periodsQuery.isLoading}
        onTabChange={handleLeftTab}
        onFyChange={handleFyChange}
      />
      <ViewSelectorCard
        side="right"
        title="Period — Quantity View"
        value={viewRight}
        fySelection={fyRight}
        fyOptions={fyOptions}
        fyLoading={periodsQuery.isLoading}
        onTabChange={handleRightTab}
        onFyChange={handleFyChange}
      />
    </div>
  );
}

export default ViewSelectors;
